<template>
  <div class="container-fluid mt-4">
    <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
      <div>
        <h2 class="mb-0"><i class="bi bi-graph-up-arrow me-2"></i>Evolución Financiera</h2>
        <small class="text-muted">Datos desde Google Sheets — solo lectura</small>
      </div>
      <div class="d-flex gap-2 align-items-center">
        <a
          v-if="sheetUrl"
          :href="sheetUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-outline-secondary btn-sm"
          title="Abrir en Google Sheets"
        >
          <i class="bi bi-box-arrow-up-right me-1"></i>Ver en Sheets
        </a>
        <button class="btn btn-outline-primary btn-sm" @click="cargarDatos" :disabled="cargando">
          <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-arrow-clockwise me-1"></i>
          {{ cargando ? 'Actualizando...' : 'Actualizar' }}
        </button>
      </div>
    </div>

    <!-- KPIs de hoy -->
    <div v-if="!cargando && !error && hoy" class="mb-4">
      <p class="mb-2" style="color:#6c757d; font-size:0.8rem; font-weight:600; text-transform:uppercase;">
        <i class="bi bi-calendar-check me-1"></i>Datos de hoy — {{ hoy[1] }}
      </p>
      <div class="row g-3">
        <!-- Importe Total -->
        <div class="col-6 col-md-3">
          <div
            class="card shadow-sm h-100 evo-importe-total-card"
            :class="{ 'evo-importe-record': esNuevoMaximoHistorico }"
            :style="esNuevoMaximoHistorico
              ? 'border-left:4px solid #ffc107; background:#fffbeb;'
              : 'border-left:4px solid #0d6efd; background:#fff;'"
          >
            <div class="card-body py-3">
              <div style="color:#6c757d; font-size:0.8rem;" class="mb-1">
                <i class="bi bi-wallet2 me-1"></i>Importe Total
                <span v-if="esNuevoMaximoHistorico" class="badge bg-warning text-dark ms-1">
                  <i class="bi bi-trophy-fill me-1"></i>Máx. histórico
                </span>
              </div>
              <div class="d-flex align-items-baseline flex-wrap gap-2">
                <div
                  class="evo-importe-total-valor"
                  :class="{ 'evo-importe-record__valor': esNuevoMaximoHistorico }"
                >{{ hoy[2] }}</div>
                <span
                  v-if="pctVsMaxLabel"
                  class="evo-pct-vs-max"
                  :class="pctVsMaximo >= 0 ? 'evo-pct-vs-max--up' : 'evo-pct-vs-max--down'"
                  :title="maxHistorico ? ('vs máx. histórico ' + maxHistorico[2]) : ''"
                >{{ pctVsMaxLabel }}</span>
              </div>
              <div style="color:#6c757d; font-size:0.8rem;">Sin cripto: {{ hoy[3] }}</div>
              <div v-if="maxHistorico" class="small text-muted mt-1">
                {{ esNuevoMaximoHistorico ? 'Anterior' : 'Máx. histórico' }}:
                {{ maxHistorico[2] }}
                <span v-if="maxHistorico[1]">({{ maxHistorico[1] }})</span>
              </div>
            </div>
          </div>
        </div>
        <!-- Beneficio -->
        <div class="col-6 col-md-3">
          <div class="card shadow-sm h-100" style="border-left:4px solid #198754; background:#fff;">
            <div class="card-body py-3">
              <div style="color:#6c757d; font-size:0.8rem;" class="mb-1"><i class="bi bi-graph-up me-1"></i>Beneficio Global</div>
              <div style="color:#212529; font-size:1.2rem; font-weight:700;">{{ hoy[6] }}</div>
              <div style="color:#6c757d; font-size:0.8rem;">Rentabilidad: {{ hoy[8] }}</div>
            </div>
          </div>
        </div>
        <!-- % FIRE -->
        <div class="col-6 col-md-3">
          <div class="card shadow-sm h-100" style="border-left:4px solid #dc3545; background:#fff;">
            <div class="card-body py-3">
              <div style="color:#6c757d; font-size:0.8rem;" class="mb-1"><i class="bi bi-fire me-1"></i>% FIRE Generado</div>
              <div style="color:#212529; font-size:1.2rem; font-weight:700;">{{ hoy[12] }}</div>
              <div style="color:#6c757d; font-size:0.8rem;">Retiro: {{ hoy[13] }}</div>
            </div>
          </div>
        </div>
        <!-- Cripto -->
        <div class="col-6 col-md-3">
          <div class="card shadow-sm h-100" style="border-left:4px solid #fd7e14; background:#fff;">
            <div class="card-body py-3">
              <div style="color:#6c757d; font-size:0.8rem;" class="mb-1"><i class="bi bi-currency-bitcoin me-1"></i>Cripto</div>
              <div style="color:#212529; font-size:1.2rem; font-weight:700;">{{ hoy[5] }}</div>
              <div style="color:#6c757d; font-size:0.8rem;">Propio: {{ hoy[4] }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="error" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle me-2"></i>
      <strong>No se pudieron cargar los datos.</strong> {{ error }}
      <div class="mt-2 small">
        Asegúrate de que el spreadsheet es público: en Google Sheets ve a
        <strong>Archivo → Compartir → Publicar en la web</strong> o cambia el acceso a
        <strong>"Cualquier persona con el enlace puede ver"</strong>.
      </div>
    </div>

    <!-- Tabs -->
    <ul v-if="!error" class="nav nav-tabs mb-4">
      <li class="nav-item">
        <button
          class="nav-link"
          :class="{ active: tabActiva === 'pasado' }"
          @click="tabActiva = 'pasado'"
        >
          <i class="bi bi-clock-history me-1"></i>Pasado
        </button>
      </li>
      <li class="nav-item">
        <button
          class="nav-link"
          :class="{ active: tabActiva === 'cuenta' }"
          @click="tabActiva = 'cuenta'"
        >
          <i class="bi bi-graph-up me-1"></i>Evolución cuenta
        </button>
      </li>
      <li class="nav-item">
        <button
          class="nav-link"
          :class="{ active: tabActiva === 'futuro' }"
          @click="tabActiva = 'futuro'"
        >
          <i class="bi bi-rocket-takeoff me-1"></i>Futuro
        </button>
      </li>
      <li class="nav-item">
        <button
          class="nav-link"
          :class="{ active: tabActiva === 'otros' }"
          @click="tabActiva = 'otros'"
        >
          <i class="bi bi-bullseye me-1"></i>Otros
        </button>
      </li>
      <li class="nav-item">
        <button
          class="nav-link"
          :class="{ active: tabActiva === 'rentabilidades' }"
          @click="tabActiva = 'rentabilidades'"
        >
          <i class="bi bi-table me-1"></i>Rentabilidades
        </button>
      </li>
      <li class="nav-item">
        <button
          class="nav-link"
          :class="{ active: tabActiva === 'aportaciones' }"
          @click="tabActiva = 'aportaciones'"
        >
          <i class="bi bi-cash-stack me-1"></i>Tabla de aportaciones
        </button>
      </li>
      <li class="nav-item">
        <button
          class="nav-link"
          :class="{ active: tabActiva === 'fire' }"
          @click="tabActiva = 'fire'"
        >
          <i class="bi bi-fire me-1"></i>FIRE
        </button>
      </li>
    </ul>

    <!-- Cargando -->
    <div v-if="cargando && !error" class="text-center py-5">
      <div class="spinner-border text-primary mb-3"></div>
      <p class="text-muted">Cargando datos desde Google Sheets...</p>
    </div>

    <!-- Tab Evolución cuenta -->
    <EvolucionCuenta
      v-if="!cargando && !error && tabActiva === 'cuenta'"
      :activo="tabActiva === 'cuenta'"
      :key="cuentaReloadKey"
    />

    <!-- Tab Pasado -->
    <div v-if="!cargando && !error && tabActiva === 'pasado'">
      <div class="card shadow-sm">
        <div class="card-header bg-danger text-white d-flex justify-content-between align-items-center">
          <h5 class="mb-0"><i class="bi bi-clock-history me-2"></i>Evolución histórica</h5>
          <small>Comparativa con períodos anteriores</small>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover table-sm mb-0">
              <thead class="table-dark">
                <tr>
                  <th>Período</th>
                  <th>Fecha</th>
                  <th class="text-end">Importe Total</th>
                  <th class="text-end">Sin Cripto</th>
                  <th class="text-end">Propio</th>
                  <th class="text-end">Cripto</th>
                  <th class="text-end">Beneficio</th>
                  <th class="text-end">Aportaciones</th>
                  <th class="text-end">Rentab.</th>
                  <th class="text-end">Gan. Anual Gen.</th>
                  <th class="text-end">Gan. Anual Retiro</th>
                  <th class="text-end">Gastos Anuales</th>
                  <th class="text-end">% FIRE Gen.</th>
                  <th class="text-end">% FIRE Retiro</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(fila, idx) in pasado" :key="idx" :class="idx === 0 ? 'table-primary fw-bold' : ''">
                  <td>{{ fila[0] }}</td>
                  <td class="text-nowrap">{{ fila[1] }}</td>
                  <td class="text-end text-nowrap">{{ fila[2] }}</td>
                  <td class="text-end text-nowrap">{{ fila[3] }}</td>
                  <td class="text-end text-nowrap">{{ fila[4] }}</td>
                  <td class="text-end text-nowrap">{{ fila[5] }}</td>
                  <td class="text-end text-nowrap" :class="esPositivo(fila[6]) ? 'text-success' : 'text-danger'">{{ fila[6] }}</td>
                  <td class="text-end text-nowrap">{{ fila[7] }}</td>
                  <td class="text-end text-nowrap" :class="esPositivo(fila[8]) ? 'text-success' : ''">{{ fila[8] }}</td>
                  <td class="text-end text-nowrap">{{ fila[9] }}</td>
                  <td class="text-end text-nowrap">{{ fila[10] }}</td>
                  <td class="text-end text-nowrap">{{ fila[11] }}</td>
                  <td class="text-end text-nowrap" :class="claseFirePct(fila[12])">{{ fila[12] }}</td>
                  <td class="text-end text-nowrap" :class="claseFirePct(fila[13])">{{ fila[13] }}</td>
                </tr>
                <tr v-if="pasado.length === 0">
                  <td colspan="14" class="text-center text-muted py-3">Sin datos</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab Otros -->
    <div v-if="!cargando && !error && tabActiva === 'otros'">
      <!-- Metas FIRE -->
      <div class="card shadow-sm mb-4">
        <div class="card-header d-flex align-items-center" style="background:#6f42c1; color:#fff;">
          <h5 class="mb-0"><i class="bi bi-bullseye me-2"></i>Metas FIRE</h5>
        </div>
        <div class="card-body">
          <div class="row g-3" v-if="metasFire.length > 0">
            <div v-for="(meta, idx) in metasFire" :key="idx" class="col-12 col-md-6">
              <div class="card h-100" :style="'border-left:4px solid ' + metaColor(idx) + '; background:#fff;'">
                <div class="card-body py-3">
                  <!-- Etiqueta tipo "Necesito" / "Llegar a" -->
                  <div style="color:#6c757d; font-size:0.75rem; font-weight:600; text-transform:uppercase;" class="mb-1">
                    <i class="bi bi-flag me-1"></i>{{ meta.etiqueta || 'Meta' }}
                  </div>
                  <!-- Importe objetivo -->
                  <div style="color:#212529; font-size:1.2rem; font-weight:700;" class="mb-1">
                    {{ meta.importe }}
                    <span v-if="meta.descripcion" style="color:#6c757d; font-size:0.8rem; font-weight:400;"> {{ meta.descripcion }}</span>
                  </div>
                  <!-- Fecha de alcance + Faltan -->
                  <div v-if="meta.fechaAlcance || meta.faltan" class="d-flex align-items-center gap-2 flex-wrap mt-1">
                    <span v-if="meta.fechaAlcance" style="color:#6c757d; font-size:0.82rem;">
                      <i class="bi bi-calendar-event me-1"></i>{{ meta.fechaAlcance }}
                    </span>
                    <span v-if="meta.faltan" class="badge" :style="'background:' + metaColor(idx)">
                      Faltan {{ meta.faltan }}
                    </span>
                  </div>
                  <div
                    v-if="meta.diasRestantesTexto || meta.pctConseguidoTexto"
                    class="d-flex align-items-center gap-2 flex-wrap mt-2"
                  >
                    <span v-if="meta.diasRestantesTexto" style="color:#6c757d; font-size:0.82rem;">
                      <i class="bi bi-hourglass-split me-1"></i>{{ meta.diasRestantesTexto }}
                    </span>
                    <span
                      v-if="meta.pctConseguidoTexto"
                      class="badge bg-light text-dark border"
                      title="Porcentaje de la meta ya cubierto"
                    >
                      {{ meta.pctConseguidoTexto }} conseguido
                    </span>
                  </div>
                  <div v-if="meta.pctConseguido != null" class="progress mt-2" style="height:6px;">
                    <div
                      class="progress-bar"
                      role="progressbar"
                      :style="{ width: meta.pctBarWidth + '%', background: metaColor(idx) }"
                      :aria-valuenow="Math.round(meta.pctBarWidth)"
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p v-else class="text-muted mb-0">Sin datos de metas</p>
        </div>
      </div>

      <!-- Promedios diarios -->
      <div class="card shadow-sm mb-4" v-if="promedios">
        <div class="card-header" style="background:#0dcaf0; color:#000;">
          <h5 class="mb-0"><i class="bi bi-bar-chart-line me-2"></i>Promedios diarios</h5>
        </div>
        <div class="card-body">
          <div class="row g-3 text-center">
            <div class="col-6 col-md-3">
              <div style="color:#6c757d; font-size:0.8rem;" class="mb-1">Aportación diaria</div>
              <div style="color:#212529; font-size:1.2rem; font-weight:700;">{{ promedios.aportDiaria }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:#6c757d; font-size:0.8rem;" class="mb-1">Ganancia diaria</div>
              <div style="color:#198754; font-size:1.2rem; font-weight:700;">{{ promedios.ganDiaria }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:#6c757d; font-size:0.8rem;" class="mb-1">Total diario</div>
              <div style="color:#0d6efd; font-size:1.2rem; font-weight:700;">{{ promedios.total }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:#6c757d; font-size:0.8rem;" class="mb-1">Gastos diarios</div>
              <div style="color:#dc3545; font-size:1.2rem; font-weight:700;">{{ promedios.gastosDiarios }}</div>
            </div>
          </div>
          <div v-if="promedios.pctAport || promedios.pctGan" class="row mt-3 text-center">
            <div class="col-6">
              <div style="color:#6c757d; font-size:0.8rem;" class="mb-1">% Aportaciones</div>
              <div style="color:#212529; font-size:1rem; font-weight:600;">{{ promedios.pctAport }}</div>
            </div>
            <div class="col-6">
              <div style="color:#6c757d; font-size:0.8rem;" class="mb-1">% Ganancias</div>
              <div style="color:#198754; font-size:1rem; font-weight:600;">{{ promedios.pctGan }}</div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Tab Rentabilidades -->
    <div v-if="!cargando && !error && tabActiva === 'rentabilidades'">
      <div class="card shadow-sm">
        <div class="card-header bg-dark text-white d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 class="mb-0 text-uppercase" style="letter-spacing:0.04em;">
            <i class="bi bi-graph-up me-2"></i>Tabla de rentabilidades
          </h5>
          <div class="btn-group btn-group-sm" role="group" aria-label="Modo">
            <button
              type="button"
              class="btn"
              :class="modoRent === 'euros' ? 'btn-light text-dark fw-bold' : 'btn-outline-light'"
              @click="modoRent = 'euros'"
            >
              EN EUROS (€)
            </button>
            <button
              type="button"
              class="btn"
              :class="modoRent === 'pct' ? 'btn-light text-dark fw-bold' : 'btn-outline-light'"
              @click="modoRent = 'pct'"
            >
              EN PORCENTAJE (%)
            </button>
          </div>
        </div>
        <div class="card-body p-0">
          <div v-if="cargandoRent" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary"></div>
            <p class="text-muted small mb-0 mt-2">Cargando rentabilidades…</p>
          </div>
          <div v-else-if="errorRent" class="alert alert-warning small m-3 mb-0">
            {{ errorRent }}
          </div>
          <div v-else class="table-responsive">
            <table class="table table-sm tabla-rent mb-0">
              <thead>
                <tr>
                  <th class="text-center cabecera-mes">{{ modoRent === 'euros' ? '(€)' : '(%)' }}</th>
                  <th v-for="m in mesesEtiquetas" :key="m" class="text-center cabecera-mes">{{ m }}.</th>
                  <th class="text-center cabecera-mes">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="fila in rentabilidades" :key="fila.year">
                  <th class="text-center fw-semibold col-anio">{{ fila.year }}</th>
                  <td
                    v-for="(mes, idx) in fila.meses"
                    :key="idx"
                    class="text-center text-nowrap"
                    :class="[claseRent(mes), mes && mes.mesesSalto > 1 ? 'celda-salto' : '']"
                    :title="tituloRent(mes, fila.year, idx)"
                  >
                    {{ formatRent(mes) }}
                  </td>
                  <td class="text-center text-nowrap fw-bold celda-total" :class="claseRent(fila.total)">
                    {{ formatRent(fila.total) }}
                  </td>
                </tr>
                <tr v-if="rentabilidades.length === 0">
                  <td :colspan="14" class="text-center text-muted py-3">Sin datos</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="card-footer small">
          <p v-if="maxDrawdown" class="mb-2">
            <strong>Pérdida máxima aguantada hasta la fecha: {{ formatoPct(maxDrawdown.pct) }}</strong>
            <span class="text-muted"> (maximum drawdown).</span>
          </p>
          <p v-if="maxDrawdown" class="text-muted mb-2" style="line-height:1.45;">
            Tu cartera ha resistido una caída del {{ formatoPct(maxDrawdown.pct) }} entre el
            {{ maxDrawdown.fechaInicio }} y el {{ maxDrawdown.fechaFin }}, lo que equivale a
            {{ formatoEuros(maxDrawdown.euro) }}.
            <span class="d-block mt-1">
              ¡Ánimo! Las caídas son temporales y forman parte del camino hacia la libertad financiera.
              Mantener la calma y seguir invirtiendo en momentos de bajada es clave para alcanzar el FIRE.
            </span>
          </p>
          <p class="text-muted mb-0">
            <i class="bi bi-info-circle me-1"></i>
            Patrimonio = Importe total (C) + Cripto (E). Rentabilidad mensual = variación de patrimonio
            menos la variación de aportaciones acumuladas (J), entre cierres de mes en
            <code>11.Histórico_FI</code>. El % usa el patrimonio al cierre del mes anterior.
            Si faltan meses en la hoja (p. ej. todo 2024), el primer mes con datos tras el hueco
            muestra la rentabilidad desde el último cierre disponible (celda con borde punteado).
          </p>
        </div>
      </div>
    </div>

    <!-- Tab Tabla de aportaciones -->
    <div v-if="!cargando && !error && tabActiva === 'aportaciones'">
      <div class="card shadow-sm">
        <div class="card-header bg-dark text-white d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 class="mb-0 text-uppercase" style="letter-spacing:0.04em;">
            <i class="bi bi-cash-stack me-2"></i>Tabla de aportaciones
          </h5>
          <small class="opacity-75">Euros netos aportados por mes</small>
        </div>
        <div class="card-body p-0">
          <div v-if="cargandoRent" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary"></div>
            <p class="text-muted small mb-0 mt-2">Cargando aportaciones…</p>
          </div>
          <div v-else-if="errorRent" class="alert alert-warning small m-3 mb-0">
            {{ errorRent }}
          </div>
          <div v-else class="table-responsive">
            <table class="table table-sm tabla-rent mb-0">
              <thead>
                <tr>
                  <th class="text-center cabecera-mes">(€)</th>
                  <th v-for="m in mesesEtiquetas" :key="m" class="text-center cabecera-mes">{{ m }}.</th>
                  <th class="text-center cabecera-mes">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="fila in rentabilidades" :key="fila.year">
                  <th class="text-center fw-semibold col-anio">{{ fila.year }}</th>
                  <td
                    v-for="(mes, idx) in fila.meses"
                    :key="idx"
                    class="text-center text-nowrap"
                    :class="[claseAport(mes && mes.aportMes), mes && mes.mesesSalto > 1 ? 'celda-salto' : '']"
                    :title="tituloAport(mes, fila.year, idx)"
                  >
                    {{ formatAport(mes && mes.aportMes) }}
                  </td>
                  <td class="text-center text-nowrap fw-bold celda-total" :class="claseAport(fila.total && fila.total.aport)">
                    {{ formatAport(fila.total && fila.total.aport) }}
                  </td>
                </tr>
                <tr v-if="rentabilidades.length === 0">
                  <td :colspan="14" class="text-center text-muted py-3">Sin datos</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="card-footer small">
          <p v-if="totalAportacionesHistorico != null" class="mb-2">
            <strong>Aportado en total: {{ formatAport(totalAportacionesHistorico) }} €</strong>
            <span class="text-muted"> (suma de las aportaciones netas de todos los años).</span>
          </p>
          <p class="text-muted mb-0">
            <i class="bi bi-info-circle me-1"></i>
            Aportación mensual = variación de las aportaciones acumuladas (columna J) entre cierres
            de mes en <code>11.Histórico_FI</code>. Un valor negativo es una retirada neta.
            Si faltan meses en la hoja, el primer mes con datos tras el hueco muestra lo aportado
            desde el último cierre disponible (celda con borde punteado).
          </p>
        </div>
      </div>
    </div>

    <!-- Tab FIRE -->
    <div v-if="!cargando && !error && tabActiva === 'fire'">
      <CalculadoraFire
        :patrimonio-inicial="patrimonioActualNumerico"
        externo-patrimonio
        patrimonio-sugerido-label="Evolución"
        :gasto-mes-sugerido="gastoMesSugeridoUAN"
        :gasto-mes-sugerido-label="gastoMesSugeridoUAN > 0 ? 'Último año natural' : ''"
        :saved-prefs="firePrefsRaw"
      />
    </div>

    <!-- Tab Futuro -->
    <div v-if="!cargando && !error && tabActiva === 'futuro'">
      <div class="card shadow-sm">
        <div class="card-header bg-success text-white d-flex justify-content-between align-items-center">
          <h5 class="mb-0"><i class="bi bi-rocket-takeoff me-2"></i>Proyecciones a futuro</h5>
          <small>Estimaciones basadas en rentabilidad histórica</small>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover table-sm mb-0">
              <thead class="table-dark">
                <tr>
                  <th>Horizonte</th>
                  <th>Fecha</th>
                  <th class="text-end">Importe Total</th>
                  <th class="text-end">Beneficio vs Actual</th>
                  <th class="text-end">Gan. Diaria Est.</th>
                  <th class="text-end">Gan. Anual Gen.</th>
                  <th class="text-end">Gan. Mensual Retiro</th>
                  <th class="text-end">Gastos Anuales</th>
                  <th class="text-end">% FIRE Gen.</th>
                  <th class="text-end">% FIRE Retiro</th>
                  <th class="text-end">Nº Días</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(fila, idx) in futuro" :key="idx">
                  <td class="fw-semibold">{{ fila[0] }}</td>
                  <td class="text-nowrap">{{ fila[1] }}</td>
                  <td class="text-end text-nowrap fw-bold text-success">{{ fila[2] }}</td>
                  <td class="text-end text-nowrap text-success">{{ fila[3] }}</td>
                  <td class="text-end text-nowrap">{{ fila[4] }}</td>
                  <td class="text-end text-nowrap">{{ fila[9] }}</td>
                  <td class="text-end text-nowrap">{{ fila[10] }}</td>
                  <td class="text-end text-nowrap">{{ fila[11] }}</td>
                  <td class="text-end text-nowrap" :class="claseFirePct(fila[12])">{{ fila[12] }}</td>
                  <td class="text-end text-nowrap" :class="claseFirePct(fila[13])">{{ fila[13] }}</td>
                  <td class="text-end text-nowrap text-dark">{{ fila[14] }}</td>
                </tr>
                <tr v-if="futuro.length === 0">
                  <td colspan="11" class="text-center text-muted py-3">Sin datos</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import axios from '../config/axios';
import CalculadoraFire from '../components/CalculadoraFire.vue';
import EvolucionCuenta from '../components/EvolucionCuenta.vue';
import {
  parseEuroValor,
  getMaxHistoricoFila,
  superaMaximoHistorico,
  pctVsMaximoHistorico,
  formatPctVsMaximo,
  parseMetasFire
} from '../utils/evolucionKpis';

export default {
  name: 'EvolucionView',
  components: { CalculadoraFire, EvolucionCuenta },
  setup() {
    const store = useStore();
    const firePrefsRaw = computed(() => store.state.auth.user?.fire_prefs ?? null);

    const tabActiva = ref('pasado');
    const cargando = ref(false);
    const error = ref('');
    const pasado = ref([]);
    const futuro = ref([]);
    const otros  = ref([]);
    const sheetUrl = ref('');
    const ultimoAnoNaturalTotal = ref(0);
    const cuentaReloadKey = ref(0);

    const rentabilidades = ref([]);
    const maxDrawdown = ref(null);
    const cargandoRent = ref(false);
    const errorRent = ref('');
    const rentCargada = ref(false);
    const modoRent = ref('euros');
    const mesesEtiquetas = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
    const totalAportacionesHistorico = computed(() => {
      if (!rentabilidades.value.length) return null;
      let suma = 0;
      let hay = false;
      for (const fila of rentabilidades.value) {
        const v = fila.total && Number.isFinite(fila.total.aport) ? fila.total.aport : null;
        if (v == null) continue;
        suma += v;
        hay = true;
      }
      return hay ? suma : null;
    });

    const hoy = computed(() => pasado.value.length > 0 ? pasado.value[0] : null);
    const maxHistorico = computed(() => getMaxHistoricoFila(pasado.value));
    const esNuevoMaximoHistorico = computed(() =>
      superaMaximoHistorico(hoy.value, maxHistorico.value)
    );
    const pctVsMaximo = computed(() =>
      pctVsMaximoHistorico(hoy.value, maxHistorico.value)
    );
    const pctVsMaxLabel = computed(() => formatPctVsMaximo(pctVsMaximo.value));

    // Patrimonio actual numérico (parseado desde la columna "Importe Total" de Hoy)
    // Fallback a 433000 si aún no hay datos cargados.
    const patrimonioActualNumerico = computed(() => {
      const valor = hoy.value && hoy.value[2] ? parseEuroValor(hoy.value[2]) : 0;
      return valor > 0 ? valor : 433000;
    });

    // Gasto mensual sugerido = gasto total del último año natural / 12
    const gastoMesSugeridoUAN = computed(() => {
      const total = Number(ultimoAnoNaturalTotal.value) || 0;
      return total > 0 ? total / 12 : 0;
    });

    // Estructura del rango B11:P21 (índices desde B=0):
    // Filas de meta: [0]=etiqueta, [1]=importe, [2]=descripcion,
    //   [6]=fecha alcance, [7]="Faltan" text, [8]=importe faltan/restante
    // Filas promedios: cabecera + valores con €, después fila con %
    const metasFire = computed(() => parseMetasFire(otros.value));

    const promedios = computed(() => {
      const headerIdx = otros.value.findIndex(f => f.join(' ').toLowerCase().includes('aport'));
      if (headerIdx < 0) return null;

      const val = otros.value[headerIdx + 1] || [];
      const pct = otros.value[headerIdx + 2] || [];

      // Detectar valores € posicionalmente en la fila de valores
      const euros = val.map((c, i) => ({ v: (c || '').trim(), i }))
                       .filter(x => /[\d.,]+\s*€/.test(x.v));
      // Detectar porcentajes posicionalmente en la fila de porcentajes
      const pcts  = pct.map((c, i) => ({ v: (c || '').trim(), i }))
                       .filter(x => /%/.test(x.v));

      return {
        aportDiaria:   euros[0]?.v || '',
        ganDiaria:     euros[1]?.v || '',
        total:         euros[2]?.v || '',
        gastosDiarios: euros[3]?.v || '',
        pctAport:      pcts[0]?.v  || '',
        pctGan:        pcts[1]?.v  || ''
      };
    });

    const metaColor = (idx) => ['#6f42c1','#0d6efd','#198754','#fd7e14'][idx % 4];

    const cargarDatos = async () => {
      cargando.value = true;
      error.value = '';
      try {
        const [resEvol, resUAN] = await Promise.allSettled([
          axios.get('/api/evolucion/resumen'),
          axios.get('/api/gastos/resumen/ultimo-ano-natural')
        ]);
        if (resEvol.status === 'fulfilled') {
          pasado.value = resEvol.value.data.pasado;
          otros.value  = resEvol.value.data.otros || [];
          futuro.value = resEvol.value.data.futuro;
          sheetUrl.value = resEvol.value.data.sheetUrl || '';
        } else {
          throw resEvol.reason;
        }
        if (resUAN.status === 'fulfilled') {
          const total = Number(resUAN.value.data?.totalNumero) || 0;
          ultimoAnoNaturalTotal.value = total > 0 ? total : 0;
        } else {
          ultimoAnoNaturalTotal.value = 0;
        }
      } catch (err) {
        error.value = err.response?.data?.error || 'Error de conexión con el servidor.';
      } finally {
        cargando.value = false;
      }
      if (tabActiva.value === 'rentabilidades' || tabActiva.value === 'aportaciones') {
        rentCargada.value = false;
        cargarRentabilidades(true);
      }
      if (tabActiva.value === 'cuenta') {
        cuentaReloadKey.value += 1;
      }
    };

    const esPositivo = (valor) => {
      if (!valor) return false;
      const num = parseFloat(String(valor).replace(/[^0-9,.-]/g, '').replace(',', '.'));
      return !isNaN(num) && num > 0;
    };

    const claseFirePct = (valor) => {
      if (!valor || valor === 'N/A') return 'text-muted';
      const num = parseFloat(String(valor).replace('%', '').replace(',', '.'));
      if (isNaN(num)) return '';
      if (num >= 100) return 'text-success fw-bold';
      if (num >= 80) return 'text-success';
      if (num >= 60) return 'text-warning';
      return 'text-danger';
    };

    const cargarRentabilidades = async (forzar = false) => {
      if (!forzar && (rentCargada.value || cargandoRent.value)) return;
      cargandoRent.value = true;
      errorRent.value = '';
      try {
        const res = await axios.get('/api/evolucion/rentabilidades');
        rentabilidades.value = Array.isArray(res.data.filas) ? res.data.filas : [];
        maxDrawdown.value = res.data.maxDrawdown || null;
        rentCargada.value = true;
      } catch (err) {
        errorRent.value = err.response?.data?.error || 'No se pudo cargar la tabla de rentabilidades.';
      } finally {
        cargandoRent.value = false;
      }
    };

    const formatoEuros = (n) => {
      if (n == null || !Number.isFinite(n)) return '';
      const redondeado = Math.round(n);
      const abs = Math.abs(redondeado);
      const fmt = new Intl.NumberFormat('es-ES', {
        maximumFractionDigits: 0,
        useGrouping: true
      }).format(abs).replace(/\u00A0/g, '.');
      return (redondeado < 0 ? '-' : '') + fmt;
    };

    const formatoPct = (n) => {
      if (n == null || !Number.isFinite(n)) return '';
      return new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(n) + ' %';
    };

    const formatRent = (mes) => {
      if (!mes) return '';
      if (modoRent.value === 'pct') return formatoPct(mes.pct);
      return formatoEuros(mes.delta);
    };

    const claseRent = (mes) => {
      if (!mes) return 'text-muted';
      const v = modoRent.value === 'pct' ? mes.pct : mes.delta;
      if (v == null || !Number.isFinite(v)) return 'text-muted';
      if (v > 0) return 'text-success';
      if (v < 0) return 'text-danger fw-semibold';
      return 'text-muted';
    };

    const tituloRent = (mes, year, idx) => {
      if (!mes) return '';
      if (mes.mesesSalto > 1) {
        const desde = `${mesesEtiquetas[mes.desdeMes]}. ${mes.desdeAno}`;
        const hasta = `${mesesEtiquetas[idx]}. ${year}`;
        return `Rentabilidad acumulada desde cierre ${desde} hasta ${hasta} (${mes.mesesSalto} meses sin dato intermedio en la hoja)`;
      }
      return '';
    };

    const formatAport = (n) => {
      if (n == null || !Number.isFinite(n)) return '';
      return formatoEuros(n);
    };

    const claseAport = (n) => {
      if (n == null || !Number.isFinite(n)) return 'text-muted';
      if (n > 0) return 'text-success';
      if (n < 0) return 'text-danger fw-semibold';
      return 'text-muted';
    };

    const tituloAport = (mes, year, idx) => {
      if (!mes) return '';
      if (mes.mesesSalto > 1) {
        const desde = `${mesesEtiquetas[mes.desdeMes]}. ${mes.desdeAno}`;
        const hasta = `${mesesEtiquetas[idx]}. ${year}`;
        return `Aportación neta desde cierre ${desde} hasta ${hasta} (${mes.mesesSalto} meses sin dato intermedio en la hoja)`;
      }
      return '';
    };

    watch(tabActiva, (t) => {
      if (t === 'rentabilidades' || t === 'aportaciones') cargarRentabilidades();
    });

    onMounted(cargarDatos);

    return {
      tabActiva,
      cargando,
      error,
      pasado,
      otros,
      futuro,
      sheetUrl,
      hoy,
      maxHistorico,
      esNuevoMaximoHistorico,
      pctVsMaximo,
      pctVsMaxLabel,
      patrimonioActualNumerico,
      gastoMesSugeridoUAN,
      firePrefsRaw,
      metasFire,
      promedios,
      metaColor,
      cargarDatos,
      esPositivo,
      claseFirePct,
      rentabilidades,
      maxDrawdown,
      cargandoRent,
      errorRent,
      modoRent,
      mesesEtiquetas,
      formatRent,
      claseRent,
      tituloRent,
      totalAportacionesHistorico,
      formatAport,
      claseAport,
      tituloAport,
      formatoEuros,
      formatoPct,
      cuentaReloadKey
    };
  }
};
</script>

<style scoped>
.tabla-rent {
  font-size: 0.82rem;
}
.tabla-rent thead th {
  background: #f4f6f8;
  color: #495057;
  font-weight: 600;
  letter-spacing: 0.02em;
  border-bottom: 1px solid #dee2e6;
}
.tabla-rent .cabecera-mes {
  font-size: 0.78rem;
}
.tabla-rent .col-anio {
  background: #f8f9fa;
  font-variant-numeric: tabular-nums;
}
.tabla-rent td,
.tabla-rent th {
  padding: 0.4rem 0.5rem;
  font-variant-numeric: tabular-nums;
}
.tabla-rent tbody tr:hover {
  background: #f8f9fa;
}
.tabla-rent .celda-total {
  background: #fafbfc;
}
.tabla-rent .celda-salto {
  box-shadow: inset 0 0 0 1px rgba(108, 117, 125, 0.45);
  border-radius: 2px;
}

.evo-importe-total-valor {
  color: #212529;
  font-size: 1.2rem;
  font-weight: 700;
}

.evo-importe-record {
  box-shadow: 0 0 0 1px rgba(255, 193, 7, 0.45), 0 0.25rem 0.75rem rgba(255, 193, 7, 0.2);
}

.evo-importe-record__valor {
  color: #b8860b !important;
}

.evo-pct-vs-max {
  font-size: 0.9rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.evo-pct-vs-max--up {
  color: #198754;
}

.evo-pct-vs-max--down {
  color: #dc3545;
}
</style>
